
import { useState, useEffect } from 'react';

interface BlurPlaceholderOptions {
  width?: number;
  height?: number;
  quality?: number;
}

export const useBlurPlaceholder = (
  imageUrl: string, 
  options: BlurPlaceholderOptions = {}
) => {
  const [blurDataURL, setBlurDataURL] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  const { width = 40, height = 30, quality = 0.1 } = options;

  useEffect(() => {
    if (!imageUrl) return;

    setIsGenerating(true);

    // Try to create a blur placeholder from the actual image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // Draw the image small to create pixelated effect
          ctx.filter = 'blur(2px)';
          ctx.drawImage(img, 0, 0, width, height);
          
          const dataURL = canvas.toDataURL('image/jpeg', quality);
          setBlurDataURL(dataURL);
        }
      } catch (error) {
        console.warn('Could not generate blur placeholder from image:', error);
        // Fallback to simple gray blur
        generateFallbackBlur();
      } finally {
        setIsGenerating(false);
      }
    };

    img.onerror = () => {
      console.warn('Could not load image for blur placeholder');
      generateFallbackBlur();
      setIsGenerating(false);
    };

    // For external images, we might not be able to load due to CORS
    // So we'll generate a simple placeholder instead
    if (imageUrl.startsWith('http') && !imageUrl.includes(window.location.hostname)) {
      generateFallbackBlur();
      setIsGenerating(false);
    } else {
      img.src = imageUrl;
    }

    function generateFallbackBlur() {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#f8fafc');
        gradient.addColorStop(0.5, '#e2e8f0');
        gradient.addColorStop(1, '#cbd5e1');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        
        setBlurDataURL(canvas.toDataURL('image/jpeg', quality));
      }
    }
  }, [imageUrl, width, height, quality]);

  return { blurDataURL, isGenerating };
};
